import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * FileExplorerContext - Shared state for the FileExplorer component
 *
 * Manages:
 * - View mode (table/grid)
 * - Selection (single/multi/compare)
 * - Search query
 * - Upload state
 */

const FileExplorerContext = createContext(null);

export const useFileExplorerContext = () => {
  const context = useContext(FileExplorerContext);
  if (!context) {
    throw new Error('useFileExplorerContext must be used within FileExplorerProvider');
  }
  return context;
};

export const FileExplorerProvider = ({ children, initialState = {} }) => {
  // View mode
  const [viewMode, setViewMode] = useState(initialState.viewMode || 'table');

  // Selection state
  const [selectedFiles, setSelectedFiles] = useState(initialState.selectedFiles || []);
  const [selectionMode, setSelectionMode] = useState(initialState.selectionMode || 'single');
  const [previousSelectionMode, setPreviousSelectionMode] = useState(initialState.selectionMode || 'single');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Compare mode (for dual file preview)
  const [compareMode, setCompareMode] = useState(false);

  // Synchronize selectionMode with compareMode
  useEffect(() => {
    if (compareMode) {
      // Save the current selection mode before entering compare mode
      setPreviousSelectionMode(selectionMode);
      setSelectionMode('compare');
    } else if (selectionMode === 'compare') {
      // When exiting compare mode, restore previous selection mode
      setSelectionMode(previousSelectionMode);
      // Keep only the first selected file if returning to single mode
      if (previousSelectionMode === 'single' && selectedFiles.length > 1) {
        setSelectedFiles([selectedFiles[0]]);
      }
    }
  }, [compareMode, selectionMode, previousSelectionMode, selectedFiles]);

  // Selection handlers
  const handleFileSelect = useCallback((file) => {
    if (selectionMode === 'single') {
      setSelectedFiles([file]);
    } else if (selectionMode === 'multi') {
      setSelectedFiles(prev => {
        const exists = prev.find(f => f?.file_id === file?.file_id);
        if (exists) {
          return prev.filter(f => f?.file_id !== file?.file_id);
        }
        return [...prev, file];
      });
    } else if (selectionMode === 'compare') {
      // Compare mode: support primary and secondary selection
      if (selectedFiles.length === 0) {
        setSelectedFiles([file]); // Primary
      } else if (selectedFiles.length === 1) {
        setSelectedFiles([...selectedFiles, file]); // Secondary
      } else {
        // Replace secondary
        setSelectedFiles([selectedFiles[0], file]);
      }
    }
  }, [selectionMode, selectedFiles]);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const isFileSelected = useCallback((file) => {
    return selectedFiles.some(f => f?.file_id === file?.file_id);
  }, [selectedFiles]);

  // View mode handlers
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    // Persist to localStorage
    try {
      localStorage.setItem('fileExplorer_viewMode', mode);
    } catch (e) {
      console.warn('Failed to save view mode preference:', e);
    }
  }, []);

  const value = {
    // View mode
    viewMode,
    setViewMode: handleViewModeChange,

    // Selection
    selectedFiles,
    setSelectedFiles,
    selectionMode,
    setSelectionMode,
    handleFileSelect,
    clearSelection,
    isFileSelected,

    // Compare mode
    compareMode,
    setCompareMode,

    // Search
    searchQuery,
    setSearchQuery,
  };

  return (
    <FileExplorerContext.Provider value={value}>
      {children}
    </FileExplorerContext.Provider>
  );
};
